/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  Icon,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Text,
} from '@tonic-one/react';
import { AngleRightIcon } from '@tonic-one/react-icons';
import isEqual from 'lodash/isEqual';
import React, { Key } from 'react';
import { useTranslation } from 'react-i18next';
import CasTreeTransfer, {
  TransferLeftBox,
} from 'src/components/CasTreeTransfer';
import {
  FormInput,
  FormLabel,
  TitleLabel,
} from 'src/components/Common/FormComponent';
import { Info } from 'src/components/Common/Info';
import EditableList from 'src/components/EditableList';
import TargetList from 'src/components/TargetList';
import { checkEmailFormat, generalExport } from 'src/pages/Policy/utils/fns';
import { useWhyDidYouUpdate } from 'src/utils/hooks';

import {
  isPaginationSelector,
  storeSelector,
  templateSelector,
  usePolicyContext,
} from '@Policy/store';
import {
  senderRecipientListDisabledSelector,
  vsDisarmExceptReceiverStringSelector,
  vsDisarmExceptSenderStringSelector,
  vsSpecifiedFileExtObjectsSelector,
  vsSpecifiedFileTypeSelector,
} from '@Policy/store/filters/malwareScanning';
import { useRulesStyle } from './style';
import { ExchangeOnlinePolicyItem } from '@Policy/utils/types';
import ApplyBound from '../../Components/ApplyBound';
import ImportModal from '../../Components/ImportModal';
import { TreeNode } from '@Components/Tree/type';
import { PolicyTypeEnum } from '@Policy/utils/constants';
import { PolicySubTypeEnum } from '@Types/index';
export const VsApplyBound = React.memo(() => {
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state and action from store
  const vsApplyBound = policySelector((s) => s.tmVirusscans.vsApplyBound);
  const setVsApplyBound = contextSelector((s) => s.setVsApplyBound);
  // local state
  useWhyDidYouUpdate('Malware scanning ApplyBound', { vsApplyBound });
  return (
    <ApplyBound
      applyBoundValue={vsApplyBound}
      setApplyBound={setVsApplyBound}
      applyBoundKey="vsApplyBound"
    />
  );
});
export const MalwareScanningSetting = React.memo(() => {
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state and action from store
  const vsDefaultScanningType = policySelector(
    (s) => s.tmVirusscans.vsDefaultScanningType
  );
  const vsEnableScanFileExt = policySelector(
    (s) => s.tmVirusscans.vsEnableScanFileExt
  );
  const specifiedFileExtList = contextSelector(
    vsSpecifiedFileExtObjectsSelector,
    isEqual
  );
  const selectedFileTypes = contextSelector(
    vsSpecifiedFileTypeSelector,
    isEqual
  );
  const fileTypes = contextSelector((s) => s.config.fileTypes);
  const setVsState = contextSelector((s) => s.setVsState);
  const setVsSpecifiedFileTypeObjects = contextSelector(
    (s) => s.setVsSpecifiedFileTypeObjects
  );
  const addSpecifiedFileExt = contextSelector((s) => s.addSpecifiedFileExt);
  const removeSpecifiedFileExt = contextSelector(
    (s) => s.removeSpecifiedFileExt
  );
  const handleSpecifiedFileTypeObjectsChange = React.useCallback(
    (keys: Key[]) => {
      setVsSpecifiedFileTypeObjects(keys as string[]);
    },
    []
  );
  // local state
  // keep the default selected file types
  const defaultSelectdFileTypesRef = React.useRef(selectedFileTypes);
  // React.useEffect(() => {
  //   defaultSelectdFileTypesRef.current = selectedFileTypes;
  // }, [selectedFileTypes]);
  useWhyDidYouUpdate('Malware scanning setting', {
    vsDefaultScanningType,
    vsEnableScanFileExt,
    specifiedFileExtList,
    selectedFileTypes,
    fileTypes,
  });
  return (
    <>
      <Flex mt="12px" alignItems="flex-start">
        <FormLabel label={t('policy.filesToScan')} width="200px" />
        <RadioGroup
          value={vsDefaultScanningType}
          onChange={(value: number) =>
            setVsState({ vsDefaultScanningType: Number(value) })
          }
        >
          <Stack spacing="3x" shouldWrapChildren mt="6px">
            <Radio value={0}>
              <Text lineHeight="20px">
                {t('policy.malwareScanning.scanAllFiles')}
              </Text>
            </Radio>
            <Radio value={1}>{t('policy.malwareScanning.scanTrueFile')}</Radio>
            <Radio value={2}>
              {t('policy.malwareScanning.scanSelectedFile')}
            </Radio>
          </Stack>
        </RadioGroup>
      </Flex>
      {vsDefaultScanningType === 2 && (
        <Flex ml="224px" mt="12px" direction="column">
          <CasTreeTransfer defaultValue={defaultSelectdFileTypesRef.current}>
            <TransferLeftBox
              treeData={fileTypes}
              placeholder={t('policy.placeholder.selectedTargets.items')}
              handleChange={handleSpecifiedFileTypeObjectsChange}
            />
          </CasTreeTransfer>
          <Flex alignItems="center" m="12px 0">
            <Switch
              name="vsEnableScanFileExt"
              mr="1x"
              checked={!!vsEnableScanFileExt}
              value={vsEnableScanFileExt}
              onChange={() => {
                console.log('vsEnableScanFileExt switch');
                setVsState({
                  vsEnableScanFileExt: vsEnableScanFileExt ? 0 : 1,
                });
              }}
              size="sm"
            />
            <Text fontSize="14px" lineHeight="20px">
              {t(
                'policy.malwareScanning.scanSelectedFile.customFileExtensions.title'
              )}
            </Text>
          </Flex>
          <EditableList
            placeholder={t(
              'policy.malwareScanning.scanSelectedFile.customFileExtensions.content'
            )}
            onAdd={addSpecifiedFileExt}
            onDelete={removeSpecifiedFileExt}
            list={specifiedFileExtList}
            limit={-1}
            size={4}
            width="520px"
            disabled={!vsEnableScanFileExt}
            description=""
          />
        </Flex>
      )}
    </>
  );
});
export const VsDetectionSettings = React.memo(
  ({ enableScanMessageBodyShow }: { enableScanMessageBodyShow: boolean }) => {
    const { t } = useTranslation();
    // get store from context
    const contextStore = usePolicyContext();
    // get store selector
    const contextSelector = storeSelector(contextStore);
    const policySelector = templateSelector(
      contextStore
    )<ExchangeOnlinePolicyItem>;
    // get state and action from store
    const vsEnablePredictiveMachineLearning = policySelector(
      (s) => s.tmVirusscans.vsEnablePredictiveMachineLearning
    );
    const vsEnableScanMessagebody = policySelector(
      (s) => s.tmVirusscans.vsEnableScanMessagebody
    );
    const vsEnableIntellitrap = policySelector(
      (s) => s.tmVirusscans.vsEnableIntellitrap
    );
    const vsEnablePredictiveMachineLearningFeedback = policySelector(
      (s) => s.tmVirusscans.vsEnablePredictiveMachineLearningFeedback
    );
    const vsDisarmEnabled = policySelector(
      (s) => s.tmVirusscans.vsDisarmEnabled
    );
    const vsNonOfficeDisarmEnabled = policySelector(
      (s) => s.tmVirusscans.vsNonOfficeDisarmEnabled
    );
    const vsDisarmExceptEnabled = policySelector(
      (s) => s.tmVirusscans.vsDisarmExceptEnabled
    );
    const activeContentSanitizingExceptionListFeatureShow = contextSelector(
      (s) =>
        s.config.featureShow.activeContentSanitizingExceptionListFeatureShow
    );
    const setVsState = contextSelector((s) => s.setVsState);
    const setVsEnablePredictiveMachineLearning = contextSelector(
      (s) => s.setVsEnablePredictiveMachineLearning
    );
    // local state
    const { iconColor, checkboxColor } = useRulesStyle();
    // get state and action from store
    const policyType = policySelector((s) => s.policyType);
    const policySubType = policySelector((s) => s.policySubType);
    useWhyDidYouUpdate('Malware scanning DetectionSettingsPart1', {
      vsEnablePredictiveMachineLearning,
      vsEnableScanMessagebody,
      vsEnableIntellitrap,
      vsEnablePredictiveMachineLearningFeedback,
      vsDisarmEnabled,
      vsDisarmExceptEnabled,
    });
    return (
      <Stack gap="6x" my="6x">
        <Flex direction="row" alignItems="center">
          <Switch
            name="vsEnablePredictiveMachineLearning"
            mr="1x"
            checked={!!vsEnablePredictiveMachineLearning}
            value={vsEnablePredictiveMachineLearning}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setVsEnablePredictiveMachineLearning(e.target.checked ? 1 : 0);
            }}
            size="sm"
          />
          <Text fontSize="16px" lineHeight="22px">
            {t('policy.detectionSettings.machineLearning')}
          </Text>
          <Info
            iconStyle={{ color: iconColor, ml: '2x' }}
            content={t('policy.detectionSettings.machineLearningDesc')}
          />
        </Flex>
        {enableScanMessageBodyShow && (
          <Flex alignItems="center">
            <Switch
              name="vsEnableScanMessagebody"
              mr="1x"
              checked={!!vsEnableScanMessagebody}
              value={vsEnableScanMessagebody}
              onChange={() => {
                console.log('vsEnableScanMessagebody switch');
                setVsState({
                  vsEnableScanMessagebody: vsEnableScanMessagebody ? 0 : 1,
                });
              }}
              size="sm"
            />
            <Text fontSize="16px" lineHeight="22px">
              {t('policy.detectionSettings.scanMessage')}
            </Text>
          </Flex>
        )}
        <Flex alignItems="center">
          <Switch
            name="vsEnableIntellitrap"
            mr="1x"
            checked={!!vsEnableIntellitrap}
            value={vsEnableIntellitrap}
            onChange={() => {
              console.log('vsEnableIntellitrap switch');
              setVsState({ vsEnableIntellitrap: vsEnableIntellitrap ? 0 : 1 });
            }}
            size="sm"
          />
          <Text fontSize="16px" lineHeight="22px">
            {t('policy.detectionSettings.enableIntelliTrap')}
          </Text>
          <Info
            iconStyle={{ color: iconColor, ml: '2x' }}
            content={t('policy.detectionSettings.enableIntelliTrapDesc')}
          />
        </Flex>
        <Flex alignItems="center">
          <Switch
            name="vsEnablePredictiveMachineLearningFeedback"
            mr="1x"
            checked={!!vsEnablePredictiveMachineLearningFeedback}
            value={vsEnablePredictiveMachineLearningFeedback}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setVsState({
                vsEnablePredictiveMachineLearningFeedback: e.target.checked
                  ? 1
                  : 0,
              });
            }}
            size="sm"
          />
          <Text fontSize="16px" lineHeight="22px">
            {t('policy.detectionSettings.allowToCollect')}
          </Text>
        </Flex>
        {[PolicyTypeEnum.ExchangeInline, PolicyTypeEnum.Exchange].includes(
          policyType
        ) && (
          <Stack gap="4x">
            <Flex alignItems="center">
              <Text fontSize="16px" lineHeight="22px">
                {t('policy.threatType.activeContent')}
              </Text>
              <Info
                iconStyle={{ color: iconColor, ml: '2x' }}
                content={t('policy.detectionSettings.detectActiveContentDesc')}
              />
            </Flex>
            <Flex alignItems="center">
              <Switch
                name="vsDisarmEnabled"
                mr="1x"
                checked={!!vsDisarmEnabled}
                value={vsDisarmEnabled}
                onChange={() => {
                  setVsState({ vsDisarmEnabled: vsDisarmEnabled ? 0 : 1 });
                }}
                size="sm"
              />
              <Text fontSize="16px" lineHeight="22px">
                {t('policy.detectionSettings.detectActiveContent')}
              </Text>
            </Flex>
            <Flex alignItems="center">
              <Switch
                name="vsNonOfficeDisarmEnabled"
                mr="1x"
                checked={!!vsNonOfficeDisarmEnabled}
                value={vsNonOfficeDisarmEnabled}
                onChange={() => {
                  setVsState({
                    vsNonOfficeDisarmEnabled: vsNonOfficeDisarmEnabled ? 0 : 1,
                  });
                }}
                size="sm"
              />
              <Text fontSize="16px" lineHeight="22px">
                {t('policy.detectionSettings.detectActiveContentNonOffice')}
                <Info
                  iconStyle={{ color: iconColor, ml: '2x' }}
                  content={t(
                    'policy.detectionSettings.detectActiveContentNonOfficeDesc'
                  )}
                />
              </Text>
            </Flex>
          </Stack>
        )}
        {activeContentSanitizingExceptionListFeatureShow &&
          ([PolicyTypeEnum.Exchange].includes(policyType) ||
            (policyType === PolicyTypeEnum.ExchangeInline &&
              policySubType === PolicySubTypeEnum.inbound)) && (
            <Flex alignItems="center" ml="2x">
              <Checkbox
                checked={!!vsDisarmExceptEnabled}
                disabled={!vsDisarmEnabled && !vsNonOfficeDisarmEnabled}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setVsState({
                    vsDisarmExceptEnabled: e.target.checked ? 1 : 0,
                  });
                }}
              >
                <Text color={checkboxColor}>
                  {t(
                    'policy.detectionSettings.detectActiveContent.enableExceptionList'
                  )}
                </Text>
              </Checkbox>
            </Flex>
          )}
      </Stack>
    );
  }
);
export const VsExceptSenderList = React.memo(() => {
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(contextStore);
  // get state and action from store
  const policyType = policySelector((s) => s.policyType);
  const availableTargets = contextSelector(
    (s) => s.availableTargetsGerneral,
    isEqual
  );
  const isPagination = contextSelector(isPaginationSelector, isEqual);
  const exceptSenders = contextSelector(
    vsDisarmExceptSenderStringSelector,
    isEqual
  );
  const addVsExceptSenders = contextSelector((s) => s.addVsExceptSenders);
  const removeVsExceptSenders = contextSelector((s) => s.removeVsExceptSenders);
  const importVsExceptSenders = contextSelector((s) => s.importVsExceptSenders);

  const getSpecificTarget = contextSelector(
    (s) => s.getSpecificTargetByService
  );
  const senderRecipientListDisabled = contextSelector(
    senderRecipientListDisabledSelector,
    isEqual
  );
  const activeContentSanitizingExceptionListFeatureShow = contextSelector(
    (s) => s.config.featureShow.activeContentSanitizingExceptionListFeatureShow
  );
  // local state
  const [vsDisarmExceptSenderEmail, setVsDisarmExceptSenderEmail] =
    React.useState<string>('');
  const defaultExceptSendersRef = React.useRef(exceptSenders.map((s) => s.key));
  const [selectedExceptSenders, setSelectedExceptSenders] = React.useState<
    string[]
  >([]);
  // const handleExceptSenderSelect = React.useCallback((keys: Key[]) => {
  //   setSelectedExceptSenders(keys as string[]);
  // }, []);
  const handleExceptSenderSelect = React.useCallback((entries: TreeNode[]) => {
    const keys = entries
      .map((entry) => {
        if (policyType === PolicyTypeEnum.ExchangeInline) {
          return entry.mail ?? entry.key;
        } else {
          return entry.key;
        }
      })
      .filter((item) => !!item);
    setSelectedExceptSenders(keys as string[]);
  }, []);
  const handleExceptSenderRightClick = React.useCallback(() => {
    // filter out the group ids
    const validSenders = selectedExceptSenders.filter((key) =>
      checkEmailFormat(key)
    );
    addVsExceptSenders(validSenders);
  }, [selectedExceptSenders]);
  const handleExceptSenderAddClick = React.useCallback(() => {
    addVsExceptSenders([vsDisarmExceptSenderEmail]);
    setVsDisarmExceptSenderEmail('');
  }, [vsDisarmExceptSenderEmail]);
  // available senders import and export
  const [
    isExceptSendersImportModalVisible,
    setIsExceptSendersImportModalVisible,
  ] = React.useState(false);
  const handleExceptSendersExport = React.useCallback((entries: any) => {
    generalExport({
      entries: entries.map((entry: any) => entry.key),
      name: 'Except-senders',
    }).then();
  }, []);
  const handleExceptSendersImportModalOpen = React.useCallback(() => {
    setIsExceptSendersImportModalVisible(true);
  }, []);
  const handleExceptSendersImportModalClose = React.useCallback(() => {
    setIsExceptSendersImportModalVisible(false);
  }, []);
  useWhyDidYouUpdate('VsExceptSenderList', {
    availableTargets,
    isPagination,
    exceptSenders,
    senderRecipientListDisabled,
    vsDisarmExceptSenderEmail,
    defaultExceptSendersRef,
    selectedExceptSenders,
    handleExceptSenderRightClick,
    isExceptSendersImportModalVisible,
  });
  return (
    <>
      {activeContentSanitizingExceptionListFeatureShow ? (
        <Box>
          <Flex mt="6x" alignItems="flex-start">
            <FormLabel
              label={t(
                'policy.detectionSettings.detectActiveContent.exceptionList.senders'
              )}
              required
            />
            <Flex direction="column">
              <Flex mb="3x">
                <FormInput
                  placeholder={t(
                    'policy.detectionSettings.detectActiveContent.exceptionList.senderObject'
                  )}
                  value={vsDisarmExceptSenderEmail}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setVsDisarmExceptSenderEmail(e.target.value)
                  }
                  width="458px"
                />
                <Button
                  variant="default"
                  ml="3x"
                  width="50px"
                  disabled={
                    senderRecipientListDisabled ||
                    vsDisarmExceptSenderEmail.length === 0
                  }
                  onClick={handleExceptSenderAddClick}
                >
                  {t('common.action.label.add')}
                </Button>
              </Flex>
              <Flex alignItems="center">
                <CasTreeTransfer
                  defaultValue={selectedExceptSenders}
                  disabled={senderRecipientListDisabled}
                  isLeftOnly
                >
                  <TransferLeftBox
                    treeData={availableTargets}
                    title={t(
                      'policy.detectionSettings.detectActiveContent.exceptionList.senderAvailable'
                    )}
                    placeholder={t(
                      'policy.detectionSettings.detectActiveContent.exceptionList.senderEmail'
                    )}
                    handleSelectedItemsChange={handleExceptSenderSelect}
                    onExpand={getSpecificTarget}
                  />
                </CasTreeTransfer>
                <Flex
                  direction="column"
                  alignItems="center"
                  justifyContent="center"
                  ml="12px"
                  mr="12px"
                  width="24px"
                >
                  <Button
                    variant="secondary"
                    onClick={handleExceptSenderRightClick}
                    mb="8px"
                    width="24px"
                    size="sm"
                  >
                    <Icon
                      as={AngleRightIcon}
                      color={
                        selectedExceptSenders.length > 0
                          ? 'blue:50'
                          : 'white:primary'
                      }
                    />
                  </Button>
                </Flex>
                <TargetList
                  disabled={false}
                  list={exceptSenders}
                  onDelete={removeVsExceptSenders}
                  limit={500}
                  width="520px"
                  size={10}
                  title={t(
                    'policy.detectionSettings.detectActiveContent.exceptionList.senderSelectedSenders'
                  )}
                  onImport={handleExceptSendersImportModalOpen}
                  onExport={handleExceptSendersExport}
                />
              </Flex>
            </Flex>
          </Flex>
          <ImportModal
            isVisible={isExceptSendersImportModalVisible}
            onClose={handleExceptSendersImportModalClose}
            onImport={importVsExceptSenders}
          />
        </Box>
      ) : null}
    </>
  );
});
export const VsExceptRecipientList = React.memo(() => {
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(contextStore);
  // get state and action from store
  const policyType = policySelector((s) => s.policyType);
  const availableTargets = contextSelector(
    (s) => s.availableTargetsGerneral,
    isEqual
  );
  const isPagination = contextSelector(isPaginationSelector, isEqual);
  const exceptRecipients = contextSelector(
    vsDisarmExceptReceiverStringSelector,
    isEqual
  );
  const addVsExceptRecipients = contextSelector((s) => s.addVsExceptRecipients);
  const removeVsExceptRecipient = contextSelector(
    (s) => s.removeVsExceptRecipient
  );
  const importVsExceptRecipients = contextSelector(
    (s) => s.importVsExceptRecipients
  );

  const getSpecificTarget = contextSelector(
    (s) => s.getSpecificTargetByService
  );
  const senderRecipientListDisabled = contextSelector(
    senderRecipientListDisabledSelector,
    isEqual
  );
  const activeContentSanitizingExceptionListFeatureShow = contextSelector(
    (s) => s.config.featureShow.activeContentSanitizingExceptionListFeatureShow
  );
  // local state
  const [vsDisarmExceptReceiverEmail, setVsDisarmExceptReceiverEmail] =
    React.useState<string>('');
  const defaultExceptRecipientsRef = React.useRef(
    exceptRecipients.map((s) => s.key)
  );
  const [selectedExceptRecipients, setSelectedExceptRecipients] =
    React.useState<string[]>([]);
  // const handleExceptRecipientSelect = React.useCallback((keys: Key[]) => {
  //   setSelectedExceptRecipients(keys as string[]);
  // }, []);
  const handleExceptRecipientSelect = React.useCallback(
    (entries: TreeNode[]) => {
      const keys = entries
        .map((entry) => {
          if (policyType === PolicyTypeEnum.ExchangeInline) {
            return entry.mail ?? entry.key;
          } else {
            return entry.key;
          }
        })
        .filter((item) => !!item);
      setSelectedExceptRecipients(keys as string[]);
    },
    []
  );
  const handleExceptRecipientRightClick = React.useCallback(() => {
    const validRecipients = selectedExceptRecipients.filter((key) =>
      checkEmailFormat(key)
    );
    addVsExceptRecipients(validRecipients);
  }, [selectedExceptRecipients]);
  const handleExceptRecipientAddClick = React.useCallback(() => {
    addVsExceptRecipients([vsDisarmExceptReceiverEmail]);
    setVsDisarmExceptReceiverEmail('');
  }, [vsDisarmExceptReceiverEmail]);
  // Except recipients import and export
  const [
    isExceptRecipientsImportModalVisible,
    setIsExceptRecipientsImportModalVisible,
  ] = React.useState(false);
  const handleExceptRecipientsExport = React.useCallback((entries: any) => {
    generalExport({
      entries: entries.map((entry: any) => entry.key),
      name: 'Except-recipients',
    }).then();
  }, []);
  const handleExceptRecipientsImportModalOpen = React.useCallback(() => {
    setIsExceptRecipientsImportModalVisible(true);
  }, []);
  const handleExceptRecipientsImportModalClose = React.useCallback(() => {
    setIsExceptRecipientsImportModalVisible(false);
  }, []);
  useWhyDidYouUpdate('VsExceptRecipientList', {
    availableTargets,
    isPagination,
    exceptRecipients,
    senderRecipientListDisabled,
    defaultExceptRecipientsRef,
    vsDisarmExceptReceiverEmail,
    selectedExceptRecipients,
    handleExceptRecipientRightClick,
    isExceptRecipientsImportModalVisible,
  });
  return (
    <>
      {activeContentSanitizingExceptionListFeatureShow ? (
        <Box>
          <Flex mt="6x" alignItems="flex-start">
            <FormLabel
              label={t(
                'policy.detectionSettings.detectActiveContent.exceptionList.recipients'
              )}
              required
            />
            <Flex direction="column">
              <Flex mb="3x">
                <FormInput
                  placeholder={t(
                    'policy.detectionSettings.detectActiveContent.exceptionList.senderObject'
                  )}
                  value={vsDisarmExceptReceiverEmail}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setVsDisarmExceptReceiverEmail(e.target.value)
                  }
                  width="458px"
                />
                <Button
                  variant="default"
                  ml="3x"
                  width="50px"
                  disabled={
                    senderRecipientListDisabled ||
                    vsDisarmExceptReceiverEmail.length === 0
                  }
                  onClick={handleExceptRecipientAddClick}
                >
                  {t('common.action.label.add')}
                </Button>
              </Flex>
              <Flex alignItems="center">
                <CasTreeTransfer
                  disabled={senderRecipientListDisabled}
                  defaultValue={selectedExceptRecipients}
                  isLeftOnly
                >
                  <TransferLeftBox
                    treeData={availableTargets}
                    title={t(
                      'policy.detectionSettings.detectActiveContent.exceptionList.recipientAvailable'
                    )}
                    placeholder={t(
                      'policy.detectionSettings.detectActiveContent.exceptionList.recipientObject'
                    )}
                    handleSelectedItemsChange={handleExceptRecipientSelect}
                    onExpand={getSpecificTarget}
                  />
                </CasTreeTransfer>
                <Flex
                  direction="column"
                  alignItems="center"
                  justifyContent="center"
                  ml="12px"
                  mr="12px"
                  width="24px"
                >
                  <Button
                    variant="secondary"
                    onClick={handleExceptRecipientRightClick}
                    mb="8px"
                    width="24px"
                    size="sm"
                  >
                    <Icon
                      as={AngleRightIcon}
                      color={
                        selectedExceptRecipients.length > 0
                          ? 'blue:50'
                          : 'white:primary'
                      }
                    />
                  </Button>
                </Flex>
                <TargetList
                  disabled={false}
                  list={exceptRecipients}
                  onDelete={removeVsExceptRecipient}
                  limit={500}
                  width="520px"
                  size={10}
                  title={t(
                    'policy.detectionSettings.detectActiveContent.exceptionList.recipientSelectedSenders'
                  )}
                  onImport={handleExceptRecipientsImportModalOpen}
                  onExport={handleExceptRecipientsExport}
                />
              </Flex>
            </Flex>
          </Flex>
          <ImportModal
            isVisible={isExceptRecipientsImportModalVisible}
            onClose={handleExceptRecipientsImportModalClose}
            onImport={importVsExceptRecipients}
          />
        </Box>
      ) : null}
    </>
  );
});

const Rules = ({
  applyBoundShow = true,
  enableScanMessageBodyShow = true,
}: {
  applyBoundShow: boolean;
  enableScanMessageBodyShow?: boolean;
}) => {
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(contextStore);
  // get state and action from store
  const policyType = policySelector((s) => s.policyType);
  const policySubType = policySelector((s) => s.policySubType);
  const activeContentSanitizingExceptionListFeatureShow = contextSelector(
    (s) => s.config.featureShow.activeContentSanitizingExceptionListFeatureShow
  );
  return (
    <div>
      <Box ml="1px" mt="16px">
        {applyBoundShow ? <VsApplyBound /> : null}
        <MalwareScanningSetting />
        <Divider mt="32px" mb="32px" />
        <TitleLabel title={t('policy.detectionSettings.title')} />
        <VsDetectionSettings
          enableScanMessageBodyShow={enableScanMessageBodyShow}
        />
        {activeContentSanitizingExceptionListFeatureShow &&
        ([PolicyTypeEnum.Exchange].includes(policyType) ||
          (policyType === PolicyTypeEnum.ExchangeInline &&
            policySubType === PolicySubTypeEnum.inbound)) ? (
          <Box ml="6x">
            <VsExceptSenderList />
            <VsExceptRecipientList />
          </Box>
        ) : null}
      </Box>
    </div>
  );
};
export default React.memo(Rules);
